import React, { useEffect, useState } from "react";
import { Download, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminHeader } from "@/components/AdminHeader";

const SIGNS = ["aries", "taurus", "gemini", "cancer", "leo", "virgo", "libra", "scorpio", "sagittarius", "capricorn", "aquarius", "pisces"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const select = "h-10 rounded-full border border-slate-300 bg-white px-4 text-[13px] font-mono text-ink focus:outline-none focus:border-gold";
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const fmtTs = (ts) => (ts ? ts.slice(0, 16).replace("T", " ") : "—");

export default function AdminSubscribers() {
  const [filters, setFilters] = useState({ sign: "", month: "", consent: "" });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/admin/subscribers", { params })
      .then((r) => { if (active) { setRows(r.data.subscribers); setTotal(r.data.total); } })
      .catch(() => active && toast.error("Could not load subscribers"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/subscribers/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `oddlytrue-subscribers-${new Date().toISOString().slice(0, 10)}.csv` });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Exported ${total} subscribers`);
    } catch { toast.error("Export failed"); }
  };

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-subscribers-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-ink">Subscribers</h1>
            <p className="mt-1 font-mono text-xs text-ink-muted" data-testid="subscribers-total"><Users className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />{total} matching</p>
          </div>
          <button data-testid="subscribers-export" onClick={exportCsv} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-white font-mono text-sm hover:bg-gold hover:text-ink transition-colors">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <select data-testid="filter-sign" value={filters.sign} onChange={set("sign")} className={select}>
            <option value="">All signs</option>{SIGNS.map((s) => <option key={s} value={s}>{cap(s)}</option>)}
          </select>
          <select data-testid="filter-month" value={filters.month} onChange={set("month")} className={select}>
            <option value="">All birth months</option>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select data-testid="filter-consent" value={filters.consent} onChange={set("consent")} className={select}>
            <option value="">Any consent</option><option value="yes">Consented</option><option value="no">No consent</option>
          </select>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="subscribers-table">
            <thead className="bg-slate-50 text-left font-mono text-[11px] uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-5 py-4">Email</th><th className="px-5 py-4">First name</th><th className="px-5 py-4">DOB</th>
                <th className="px-5 py-4">Star sign</th><th className="px-5 py-4">Consent</th><th className="px-5 py-4 hidden lg:table-cell">Consent at</th><th className="px-5 py-4 hidden md:table-cell">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center font-mono text-xs text-ink-muted">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center font-mono text-xs text-ink-muted" data-testid="subscribers-empty">No subscribers match these filters.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} data-testid={`subscriber-row-${r.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-ink">{r.email}</td>
                  <td className="px-5 py-3 text-ink">{r.first_name || "—"}</td>
                  <td className="px-5 py-3 font-mono text-xs text-ink">{r.dob || "—"}</td>
                  <td className="px-5 py-3 text-ink">{r.star_sign || "—"}</td>
                  <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded-full font-mono text-[11px] ${r.marketing_consent ? "bg-gold/20 text-ink" : "bg-slate-100 text-ink-muted"}`}>{r.marketing_consent ? "yes" : "no"}</span></td>
                  <td className="px-5 py-3 font-mono text-xs text-ink-muted hidden lg:table-cell">{fmtTs(r.consent_timestamp)}</td>
                  <td className="px-5 py-3 font-mono text-xs text-ink-muted hidden md:table-cell">{r.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
